/**
 * Client-side device preferences — theme, motion, confirmation dialogs.
 *
 * Deliberately separate from `GameProvider`/`GameStore`: these are per-device
 * UI choices, not game-authoritative state, so they have no business in
 * `packages/engine`'s `GameState` or going through the cloud-save pipeline.
 * LocalStorage only, and deliberately so — a theme/motion preference syncing
 * across devices isn't worth the extra API surface.
 */

export interface Settings {
  /** Cosmetic re-skin — matches `data-sg-theme` in @spacegame/ui's tokens. */
  theme: 'dark' | 'command';
  /** Forces every `prefers-reduced-motion`-gated animation off, regardless of the OS setting. */
  reducedMotion: boolean;
  /**
   * Skips the confirmation dialog before spending Chronos-Kristalle on a
   * build-time boost ("Zeitsprung"). Deliberately does NOT extend to the
   * Chronos-Shop checkout — that stays confirmed every time, since it is
   * modelled on a real-money purchase.
   */
  skipBoostConfirm: boolean;
  /** Language of the tutorial/help-center content (see apps/web/src/tutorial/). */
  locale: 'de' | 'en';
  /**
   * Has the first-run welcome screen already been shown on this device?
   * The Help Center and the "Tour neu starten" action in Settings stay
   * reachable at any time regardless of this flag.
   */
  tutorialSeen: boolean;
}

export const DEFAULT_SETTINGS: Settings = {
  theme: 'dark',
  reducedMotion: false,
  skipBoostConfirm: false,
  locale: 'de',
  tutorialSeen: false,
};

const STORAGE_KEY = 'dysonwarfare:settings:v1';

export function loadSettings(): Settings {
  if (typeof window === 'undefined') return DEFAULT_SETTINGS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw) as Partial<Settings>;
    return { ...DEFAULT_SETTINGS, ...parsed };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

export function saveSettings(settings: Settings): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Storage full or blocked (private browsing) — settings just won't
    // persist across reloads. Not worth surfacing as an error.
  }
}

/** Applies theme/motion as DOM attributes — the one place CSS reads them from. */
export function applySettingsToDom(settings: Settings): void {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.sgTheme = settings.theme;
  if (settings.reducedMotion) {
    document.documentElement.dataset.motion = 'reduced';
  } else {
    delete document.documentElement.dataset.motion;
  }
}
